import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { WizardContainer, WizardNavigation, WizardSummary, WizardSuccess } from '@/components/wizards';
import { Card, ErrorSummary, Input, Select } from '@/components/common';
import { useWizard } from '@/hooks/useWizard';
import { useAuthStore } from '@/store';
import api, { getErrorMessage, getFieldErrors } from '@/services/api';

/**
 * Board #209 -- Persist and Auto-Link Onboarding Accounts.
 *
 * WHAT THIS USED TO BE: `onComplete` was
 * `await new Promise(r => setTimeout(r, 1000))`, a
 * `toast.success('Onboarding completed successfully!')` and a success
 * screen. No API call of any kind. Country admins were told countries,
 * insurers and brokers had been created when nothing had, which is why the
 * route was withdrawn from App.tsx rather than left reachable.
 *
 * WHAT IT IS NOW: four real writes, in order, through the endpoints that
 * already own each object -- nothing here reimplements provisioning:
 *
 *   1. POST /insurers/insurers/            -> the insurer
 *   2. POST /insurers/insurers/{id}/create_user/
 *                                          -> its admin account (the #203
 *                                             primitive: validates the
 *                                             password, normalises the phone)
 *   3. POST /brokers/                      -> the broker, which server-side
 *                                             already creates the broker_admin
 *                                             User + BrokerUser(ADMIN) +
 *                                             InviteToken and emails the invite
 *                                             (BrokerViewSet._provision_broker_admin)
 *   4. POST /insurers/insurers/{id}/assign_brokers/
 *                                          -> THE LINK: an ACTIVE
 *                                             BrokerInsurerRelation between the
 *                                             two accounts just created
 *
 * Each step records what it actually created, and a failure stops the run
 * there: no success screen, the API's own message, and an explicit list of
 * what already exists so the half-finished state is visible instead of being
 * an orphan nobody knows about.
 *
 * SCOPE, stated rather than glossed over: the wizard works inside the
 * signed-in admin's OWN country. It does not create countries or country
 * admin accounts, because the endpoints for those cannot do it correctly from
 * here -- `POST /geography/countries/` is super-admin-only, and `POST /users/`
 * stamps a new user's country from the REQUESTER (`AdminUserCreateSerializer.
 * create`), so a super admin with no country of their own would create a
 * country admin with no country, i.e. an account that cannot then create
 * brokers. `POST /brokers/` has the same constraint from the other side: it
 * files the broker under `request.user.country_id` and refuses outright when
 * that is empty. So the wizard refuses to start without a country rather than
 * producing accounts that look created and do not work.
 */

type Category = 'life' | 'general' | 'health' | 'composite';

interface InsurerStepData {
  name: string;
  code: string;
  category: Category;
  email: string;
  phone: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;
  admin_phone: string;
  admin_password: string;
}

interface BrokerStepData {
  name: string;
  code: string;
  email: string;
  phone: string;
  license_number: string;
}

interface OnboardingData {
  insurer: InsurerStepData;
  broker: BrokerStepData;
}

interface CreatedRecord {
  label: string;
  detail: string;
}

const steps = [
  { id: 'country', title: 'Country' },
  { id: 'insurer', title: 'Insurer' },
  { id: 'broker', title: 'Broker' },
  { id: 'review', title: 'Review' },
];

const insurerCategories = [
  { value: 'general', label: 'General Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'health', label: 'Health Insurance' },
  { value: 'composite', label: 'Composite' },
];

const EMPTY_INSURER: InsurerStepData = {
  name: '',
  code: '',
  category: 'general',
  email: '',
  phone: '',
  admin_first_name: '',
  admin_last_name: '',
  admin_email: '',
  admin_phone: '',
  admin_password: '',
};

const EMPTY_BROKER: BrokerStepData = {
  name: '',
  code: '',
  email: '',
  phone: '',
  license_number: '',
};

/** Every message a rejected write carries, field-keyed ones included. */
function messagesFor(error: unknown): string[] {
  const fieldErrors = getFieldErrors(error);
  const values = Object.values(fieldErrors);
  return values.length > 0 ? values : [getErrorMessage(error)];
}

function isTenDigits(value: string) {
  return /^\d{10}$/.test(value.replace(/[\s-]/g, ''));
}

function CountryStep({ countryName, countryCode }: { countryName: string; countryCode: string }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Country</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Onboarding runs inside the country your account administers. The insurer and broker
          created below are filed under it.
        </p>
      </div>
      <Card padding="md">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Country</dt>
            <dd className="text-sm text-gray-900 dark:text-white mt-0.5">{countryName}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Code</dt>
            <dd className="text-sm text-gray-900 dark:text-white mt-0.5">{countryCode || '-'}</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

function InsurerStep({
  data,
  onChange,
  errors,
}: {
  data: InsurerStepData;
  onChange: (next: InsurerStepData) => void;
  errors: Record<string, string>;
}) {
  const set = (field: keyof InsurerStepData, value: string) =>
    onChange({ ...data, [field]: value } as InsurerStepData);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Insurer</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          The insurance company, plus the admin account that will sign in for it.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          name="insurer_name"
          label="Insurer Name"
          placeholder="e.g. ICICI Lombard"
          value={data.name}
          onChange={(e) => set('name', e.target.value)}
          error={errors['insurer.name']}
          required
        />
        <Input
          name="insurer_code"
          label="Insurer Code"
          placeholder="e.g. ICICIL"
          value={data.code}
          onChange={(e) => set('code', e.target.value.toUpperCase())}
          error={errors['insurer.code']}
          required
        />
        <Input
          name="insurer_email"
          label="Insurer Email"
          type="email"
          placeholder="contact@insurer.com"
          value={data.email}
          onChange={(e) => set('email', e.target.value)}
          error={errors['insurer.email']}
          required
        />
        <Input
          name="insurer_phone"
          label="Insurer Phone"
          placeholder="10 digits"
          value={data.phone}
          onChange={(e) => set('phone', e.target.value)}
          error={errors['insurer.phone']}
          required
        />
        <Select
          name="insurer_category"
          label="Category"
          options={insurerCategories}
          value={data.category}
          onChange={(e) => set('category', e.target.value)}
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Insurer Admin Account</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4">
          Same rules as Add User in User Management — a real password and a 10-digit mobile.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            name="admin_first_name"
            label="Admin First Name"
            value={data.admin_first_name}
            onChange={(e) => set('admin_first_name', e.target.value)}
            error={errors['insurer.admin_first_name']}
            required
          />
          <Input
            name="admin_last_name"
            label="Admin Last Name"
            value={data.admin_last_name}
            onChange={(e) => set('admin_last_name', e.target.value)}
            error={errors['insurer.admin_last_name']}
            required
          />
          <Input
            name="admin_email"
            label="Admin Email"
            type="email"
            value={data.admin_email}
            onChange={(e) => set('admin_email', e.target.value)}
            error={errors['insurer.admin_email']}
            required
          />
          <Input
            name="admin_phone"
            label="Admin Phone"
            placeholder="10 digits"
            value={data.admin_phone}
            onChange={(e) => set('admin_phone', e.target.value)}
            error={errors['insurer.admin_phone']}
            required
          />
          <Input
            name="admin_password"
            label="Admin Password"
            type="password"
            helperText="At least 8 characters. The admin changes it on first login."
            value={data.admin_password}
            onChange={(e) => set('admin_password', e.target.value)}
            error={errors['insurer.admin_password']}
            required
          />
        </div>
      </div>
    </div>
  );
}

function BrokerStep({
  data,
  onChange,
  errors,
}: {
  data: BrokerStepData;
  onChange: (next: BrokerStepData) => void;
  errors: Record<string, string>;
}) {
  const set = (field: keyof BrokerStepData, value: string) =>
    onChange({ ...data, [field]: value } as BrokerStepData);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Broker</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Creating the broker also provisions its admin account and emails a 7-day invite —
          that is handled by the broker endpoint itself, not duplicated here.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          name="broker_name"
          label="Broker Name"
          placeholder="e.g. ABC Insurance Brokers"
          value={data.name}
          onChange={(e) => set('name', e.target.value)}
          error={errors['broker.name']}
          required
        />
        <Input
          name="broker_code"
          label="Broker Code"
          placeholder="e.g. ABCIB"
          value={data.code}
          onChange={(e) => set('code', e.target.value.toUpperCase())}
          error={errors['broker.code']}
          required
        />
        <Input
          name="broker_email"
          label="Broker Email"
          type="email"
          placeholder="contact@broker.com"
          value={data.email}
          onChange={(e) => set('email', e.target.value)}
          error={errors['broker.email']}
          helperText="The broker admin account is created with this address."
          required
        />
        <Input
          name="broker_phone"
          label="Broker Phone"
          placeholder="10 digits"
          value={data.phone}
          onChange={(e) => set('phone', e.target.value)}
          error={errors['broker.phone']}
          required
        />
        <Input
          name="license_number"
          label="License Number"
          placeholder="IRDAI licence number"
          value={data.license_number}
          onChange={(e) => set('license_number', e.target.value)}
          error={errors['broker.license_number']}
          required
        />
      </div>
    </div>
  );
}

function CreatedList({ records, heading }: { records: CreatedRecord[]; heading: string }) {
  if (records.length === 0) return null;
  return (
    <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4 text-left">
      <h3 className="text-sm font-semibold text-green-800">{heading}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-green-800">
        {records.map((record) => (
          <li key={record.label}>
            <span className="font-medium">{record.label}:</span> {record.detail}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const countryId = (user?.country_id as string | null | undefined) || null;

  const [isSuccess, setIsSuccess] = useState(false);
  const [created, setCreated] = useState<CreatedRecord[]>([]);
  const [failure, setFailure] = useState<{ step: string; messages: string[] } | null>(null);

  const { data: country } = useQuery({
    queryKey: ['onboarding-country', countryId],
    queryFn: async () => {
      const response = await api.get(`/geography/countries/${countryId}/`);
      return response.data as { id: string; name: string; code: string };
    },
    enabled: Boolean(countryId),
    staleTime: 5 * 60 * 1000,
  });

  const wizard = useWizard<OnboardingData>({
    steps,
    initialData: { insurer: { ...EMPTY_INSURER }, broker: { ...EMPTY_BROKER } },
    validateStep: (step, data) => {
      const errors: string[] = [];
      if (step === 1) {
        const insurer = data.insurer || EMPTY_INSURER;
        if (!insurer.name) errors.push('insurer.name:Insurer name is required');
        if (!insurer.code) errors.push('insurer.code:Insurer code is required');
        if (!insurer.email) errors.push('insurer.email:Insurer email is required');
        if (!isTenDigits(insurer.phone || '')) {
          errors.push('insurer.phone:Please enter a 10 digit mobile number.');
        }
        if (!insurer.admin_first_name) errors.push('insurer.admin_first_name:First name is required');
        if (!insurer.admin_last_name) errors.push('insurer.admin_last_name:Last name is required');
        if (!insurer.admin_email) errors.push('insurer.admin_email:Admin email is required');
        if (!isTenDigits(insurer.admin_phone || '')) {
          errors.push('insurer.admin_phone:Please enter a 10 digit mobile number.');
        }
        if ((insurer.admin_password || '').length < 8) {
          errors.push('insurer.admin_password:Password must be at least 8 characters');
        }
      }
      if (step === 2) {
        const broker = data.broker || EMPTY_BROKER;
        if (!broker.name) errors.push('broker.name:Broker name is required');
        if (!broker.code) errors.push('broker.code:Broker code is required');
        if (!broker.email) errors.push('broker.email:Broker email is required');
        if (!isTenDigits(broker.phone || '')) {
          errors.push('broker.phone:Please enter a 10 digit mobile number.');
        }
        if (!broker.license_number) errors.push('broker.license_number:License number is required');
      }
      return errors.length === 0 ? true : errors;
    },
    onComplete: async (data) => {
      setFailure(null);
      const done: CreatedRecord[] = [];
      const record = (entry: CreatedRecord) => {
        done.push(entry);
        setCreated([...done]);
      };
      const fail = (step: string, error: unknown) => {
        setCreated([...done]);
        setFailure({ step, messages: messagesFor(error) });
      };

      const insurer = data.insurer;
      const broker = data.broker;

      // 1. Insurer
      let insurerId = '';
      try {
        const response = await api.post('/insurers/insurers/', {
          name: insurer.name,
          code: insurer.code,
          category: insurer.category,
          email: insurer.email,
          phone: insurer.phone,
          country: countryId,
        });
        insurerId = String(response.data?.id || '');
        if (!insurerId) {
          throw new Error(
            'The insurer was saved but the API returned no id, so nothing can be linked to it. Check the Insurers list before retrying.'
          );
        }
        record({ label: 'Insurer', detail: `${insurer.name} (${insurer.code})` });
      } catch (error) {
        fail('Insurer', error);
        return;
      }

      // 2. Insurer admin account
      try {
        await api.post(`/insurers/insurers/${insurerId}/create_user/`, {
          first_name: insurer.admin_first_name,
          last_name: insurer.admin_last_name,
          email: insurer.admin_email,
          phone: insurer.admin_phone,
          password: insurer.admin_password,
          role: 'ADMIN',
        });
        record({ label: 'Insurer admin', detail: insurer.admin_email });
      } catch (error) {
        fail('Insurer admin account', error);
        return;
      }

      // 3. Broker (+ its admin account and invite, server-side)
      let brokerId = '';
      try {
        const response = await api.post('/brokers/', {
          name: broker.name,
          code: broker.code,
          email: broker.email,
          phone: broker.phone,
          license_number: broker.license_number,
          status: 'active',
        });
        brokerId = String(response.data?.id || '');
        if (!brokerId) {
          throw new Error(
            'The broker was saved but the API returned no id, so it could not be linked to the insurer. Check the Brokers list before retrying.'
          );
        }
        const adminEmail = response.data?.admin_credentials?.email || broker.email;
        record({ label: 'Broker', detail: `${broker.name} (${broker.code})` });
        record({ label: 'Broker admin', detail: String(adminEmail) });
      } catch (error) {
        fail('Broker', error);
        return;
      }

      // 4. The link between the two
      try {
        const response = await api.post(`/insurers/insurers/${insurerId}/assign_brokers/`, {
          broker_ids: [brokerId],
        });
        const linked = Number(response.data?.linked ?? 0) + Number(response.data?.already_linked ?? 0);
        if (linked < 1) {
          throw new Error(
            'The link between the broker and the insurer was not created. Assign the broker from the insurer page before going live.'
          );
        }
        record({ label: 'Link', detail: `${broker.name} is now an active channel for ${insurer.name}` });
      } catch (error) {
        fail('Linking the broker to the insurer', error);
        return;
      }

      toast.success('Onboarding complete');
      setIsSuccess(true);
    },
  });

  const reviewSections = useMemo(() => {
    const insurer = wizard.data.insurer || EMPTY_INSURER;
    const broker = wizard.data.broker || EMPTY_BROKER;
    return [
      {
        title: 'Country',
        items: [
          { label: 'Country', value: country?.name || '-' },
          { label: 'Code', value: country?.code || '-' },
        ],
      },
      {
        title: 'Insurer',
        items: [
          { label: 'Name', value: insurer.name || '-' },
          { label: 'Code', value: insurer.code || '-' },
          { label: 'Category', value: insurer.category || '-' },
          { label: 'Email', value: insurer.email || '-' },
          { label: 'Phone', value: insurer.phone || '-' },
          { label: 'Admin', value: insurer.admin_email || '-' },
        ],
      },
      {
        title: 'Broker',
        items: [
          { label: 'Name', value: broker.name || '-' },
          { label: 'Code', value: broker.code || '-' },
          { label: 'Email', value: broker.email || '-' },
          { label: 'Phone', value: broker.phone || '-' },
          { label: 'License Number', value: broker.license_number || '-' },
        ],
      },
    ];
  }, [wizard.data, country]);

  if (!countryId) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card padding="lg">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Onboarding Wizard</h1>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            This account has no country assigned, and onboarding files the insurer and broker it
            creates under the country of the admin running it. Ask a super admin to assign your
            account a country, then come back — nothing has been created.
          </p>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="max-w-4xl mx-auto">
        <WizardSuccess
          title="Onboarding Complete"
          message="Everything below was created and linked. The broker admin has been emailed a 7-day invite."
          actions={
            <>
              <button
                type="button"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
                onClick={() => navigate('/admin/insurers')}
              >
                Go to Insurers
              </button>
              <button
                type="button"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
                onClick={() => navigate('/brokers')}
              >
                Go to Brokers
              </button>
            </>
          }
        />
        <CreatedList records={created} heading="Created" />
      </div>
    );
  }

  const renderStepContent = () => {
    switch (wizard.currentStep) {
      case 0:
        return <CountryStep countryName={country?.name || 'Loading…'} countryCode={country?.code || ''} />;
      case 1:
        return (
          <InsurerStep
            data={wizard.data.insurer || EMPTY_INSURER}
            onChange={(next) => wizard.setStepData('insurer', next)}
            errors={wizard.errors}
          />
        );
      case 2:
        return (
          <BrokerStep
            data={wizard.data.broker || EMPTY_BROKER}
            onChange={(next) => wizard.setStepData('broker', next)}
            errors={wizard.errors}
          />
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review &amp; Confirm</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Completing creates the insurer, its admin account, the broker and its admin
                account, then links the broker to the insurer.
              </p>
            </div>
            {failure && (
              <>
                <ErrorSummary
                  messages={[`${failure.step} failed — nothing after this step was attempted.`, ...failure.messages]}
                />
                <CreatedList records={created} heading="Already created — do not re-enter these" />
              </>
            )}
            <WizardSummary sections={reviewSections} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <WizardContainer
      title="Onboarding Wizard"
      description="Create an insurer and a broker in your country, and link them."
      steps={steps}
      currentStep={wizard.currentStep}
      onStepClick={wizard.goToStep}
    >
      {renderStepContent()}

      <WizardNavigation
        isFirst={wizard.isFirst}
        isLast={wizard.isLast}
        isSubmitting={wizard.isSubmitting}
        onPrevious={wizard.goPrevious}
        onNext={wizard.goNext}
        onComplete={wizard.complete}
        completeLabel="Complete Setup"
      />
    </WizardContainer>
  );
}

export default OnboardingWizard;
